import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import BetaBanner from './BetaBanner';
import ChangePasswordModal from './ChangePasswordModal';

const BACKEND_URL = process.env.REACT_APP_API_URL;
interface DashboardStats {
  activeLeagues: number;
  totalActivities: number;
  registeredBowlers: number;
  totalEarnings: number;
}

interface Activity {
  actionType: string;
  description: string;
  metadata: any;
  createdAt: string;
}

interface RecentLeague {
  id: number;
  name: string;
  teamFormat: string;
  startDate: string;
  endDate: string;
  createdAt: string;
}

interface DashboardData {
  stats: DashboardStats;
  recentActivity: Activity[];
  recentLeagues: RecentLeague[];
}

interface BillingStatus {
  currentDue: number;
  totalPaid: number;
  grossAmount: number;
  bowlerCount: number;
  pricePerBowler: number;
  isOverdue: boolean;
  isLocked: boolean;
  billingPeriod: {
    label: string;
    year: number;
    deadline: string;
    daysUntilDeadline: number;
  };
}

const PERIOD_KEY: Record<string, string> = {
  may: 'admin.billing.periodMay',
  september: 'admin.billing.periodSeptember',
  december: 'admin.billing.periodDecember',
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [showBillingInfo, setShowBillingInfo] = useState(false);
 
  
  // Support form state
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [supportReason, setSupportReason] = useState('');
  const [supportCategory, setSupportCategory] = useState('');
  const [supportSubcategory, setSupportSubcategory] = useState('');
  const [supportQuestion, setSupportQuestion] = useState('');
  const [submittingSupport, setSubmittingSupport] = useState(false);

  // Check for success message from navigation state
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
      // Clear the navigation state
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
        const response = await fetch(`${apiUrl}/admin/dashboard`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setDashboardData(data);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Fetch billing status
  useEffect(() => {
    const fetchBilling = async () => {
      try {
        const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
        const res = await fetch(`${apiUrl}/admin/billing/current`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (res.ok) {
          const data = await res.json();
          setBillingStatus(data);
        }
      } catch {
        // Billing not critical – fail silently
      } finally {
        setBillingLoading(false);
      }
    };
    fetchBilling();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/'); // Redirect to welcome page after logout
  };

  const handleSupportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!supportReason || !supportQuestion.trim()) {
      return;
    }
    
    setSubmittingSupport(true);
    
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/support/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          reason: supportReason,
          category: supportCategory || null,
          subcategory: supportSubcategory || null,
          questions: supportQuestion
        })
      });
      
      if (response.ok) {
        setSuccessMessage(t('admin.support.submitSuccess'));
        setShowSupportModal(false);
        setSupportReason('');
        setSupportCategory('');
        setSupportSubcategory('');
        setSupportQuestion('');
      } else {
        const errorData = await response.json();
        alert(errorData.error || t('admin.support.submitError'));
      }
    } catch (error) {
      console.error('Support submission error:', error);
      alert(t('admin.support.submitError'));
    } finally {
      setSubmittingSupport(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
      {/* Header with User Info and Logout */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center py-4 sm:py-6 gap-3">
            {/* User Info */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-base sm:text-lg">
                  {user.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div className="flex items-center space-x-2 flex-wrap">
                  <h1 className="text-lg sm:text-2xl font-bold text-white">
                    {t('admin.dashboard.welcome', { name: user.name })}
                  </h1>
                  {/* Admin Badge */}
                  <div className="flex items-center space-x-1 bg-gradient-to-r from-purple-400 to-indigo-500 px-2 sm:px-3 py-1 rounded-full">
                    <span className="text-white text-xs sm:text-sm font-semibold"><span className="hidden sm:inline">⚙️ </span>{t('admin.dashboard.administrator')}</span>
                  </div>
                </div>
                <p className="text-purple-200 text-xs sm:text-sm">
                  {t('admin.dashboard.adminConsole')} - {t('admin.dashboard.leagueManagementSystem')}
                </p>
              </div>
          
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="group flex items-center space-x-2 bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/40 px-3 py-2 sm:px-6 sm:py-3 rounded-lg transition-all duration-200 transform hover:scale-105"
            >
              <span className="hidden sm:inline text-white font-semibold">{t('common.logout')}</span>
              <svg
                className="w-5 h-5 text-white group-hover:translate-x-1 transition-transform duration-200"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Floating Support Button */}
      <button
        className="fixed bottom-5 right-6 z-50 flex items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur-md border border-white/30 text-white text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg transition duration-200"
        onClick={() => setShowSupportModal(true)}
        title="Contact Support 24/7"
      >
        <span className="text-base">🎧</span>
        <span>Contact Support 24/7</span>
      </button>
      
      
      {/* Support Modal */}
      {showSupportModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={e => {
            if (e.target === e.currentTarget) setShowSupportModal(false);
          }}
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-lg w-full mx-4 p-8 relative"
            style={{
              maxHeight: '80vh',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: '#6366f1 #18181b'
            }}>
            {/* Support Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-green-600/30 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-4xl text-green-300 font-bold">💬</span>
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-white text-center mb-6">{t('admin.support.title')}</h2>
            
            <form onSubmit={handleSupportSubmit} className="space-y-4">
              {/* Reason for Inquiry */}
              <div>
                <label className="block text-white font-semibold mb-2">
                  {t('admin.support.reasonLabel')}
                </label>
                <select
                  value={supportReason}
                  onChange={(e) => setSupportReason(e.target.value)}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="" className="bg-gray-800">{t('admin.support.selectReason')}</option>
                  <option value="Need help figuring something out" className="bg-gray-800">{t('admin.support.reasons.needHelp')}</option>
                  <option value="I am getting an error when trying to do something" className="bg-gray-800">{t('admin.support.reasons.gettingError')}</option>
                  <option value="My app keeps crashing" className="bg-gray-800">{t('admin.support.reasons.appCrashing')}</option>
                  <option value="I can't find what I am looking for" className="bg-gray-800">{t('admin.support.reasons.cantFind')}</option>
                  <option value="Something isn't working as I expected" className="bg-gray-800">{t('admin.support.reasons.notWorkingExpected')}</option>
                  <option value="Other" className="bg-gray-800">{t('admin.support.reasons.other')}</option>
                </select>
              </div>
              
              {/* Category - What part of the app */}
              <div>
                <label className="block text-white font-semibold mb-2">
                  {t('admin.support.categoryLabel')}
                </label>
                <select
                  value={supportCategory}
                  onChange={(e) => {
                    setSupportCategory(e.target.value);
                    setSupportSubcategory(''); // Reset subcategory when category changes
                  }}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="" className="bg-gray-800">{t('admin.support.selectCategory')}</option>
                  <option value="League Creation" className="bg-gray-800">{t('admin.support.categories.leagueCreation')}</option>
                  <option value="League Management" className="bg-gray-800">{t('admin.support.categories.leagueManagement')}</option>
                  <option value="Player Management" className="bg-gray-800">{t('admin.support.categories.playerManagement')}</option>
                  <option value="Analytics" className="bg-gray-800">{t('admin.support.categories.analytics')}</option>
                  <option value="Financials" className="bg-gray-800">{t('admin.support.categories.financials')}</option>
                </select>
              </div>
              
              {/* Subcategory - Only show if League Management is selected */}
              {supportCategory === 'League Management' && (
                <div>
                  <label className="block text-white font-semibold mb-2">
                    {t('admin.support.subcategoryLabel')}
                  </label>
                  <select
                    value={supportSubcategory}
                    onChange={(e) => setSupportSubcategory(e.target.value)}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="" className="bg-gray-800">{t('admin.support.selectSubcategory')}</option>
                    <option value="Check-in" className="bg-gray-800">{t('admin.support.subcategories.checkin')}</option>
                    <option value="Managing Schedule" className="bg-gray-800">{t('admin.support.subcategories.managingSchedule')}</option>
                    <option value="Managing Financials" className="bg-gray-800">{t('admin.support.subcategories.managingFinancials')}</option>
                    <option value="Managing Teams" className="bg-gray-800">{t('admin.support.subcategories.managingTeams')}</option>
                    <option value="Managing or Adding Side Events" className="bg-gray-800">{t('admin.support.subcategories.managingSideEvents')}</option>
                    <option value="All of the Above" className="bg-gray-800">{t('admin.support.subcategories.allOfTheAbove')}</option>
                    <option value="Other" className="bg-gray-800">{t('admin.support.subcategories.other')}</option>
                  </select>
                </div>
              )}
              
              {/* Question Text Area */}
              <div>
                <label className="block text-white font-semibold mb-2">
                  {t('admin.support.questionLabel')}
                </label>
                <textarea
                  value={supportQuestion}
                  onChange={(e) => setSupportQuestion(e.target.value)}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500 min-h-32"
                  placeholder={t('admin.support.questionPlaceholder')}
                  required
                />
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowSupportModal(false)}
                  className="flex-1 bg-gray-600 text-white py-2 rounded-lg font-bold hover:bg-gray-700 transition"
                >
                  {t('common.close')}
                </button>
                <button
                  type="submit"
                  disabled={submittingSupport || !supportReason || !supportQuestion.trim()}
                  className="flex-1 bg-green-600 text-white py-2 rounded-lg font-bold hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submittingSupport ? t('common.Processing') : t('common.submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid gap-8">
          {/* Beta Banner */}
          <BetaBanner />
          
          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-500/20 border border-green-400 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <div className="text-green-400 text-xl">✅</div>
                <p className="text-green-200 font-medium">{successMessage}</p>
              </div>
            </div>
          )}

          {/* ── BILLING LOCK SCREEN ─────────────────────────────────── */}
          {billingStatus?.isLocked ? (
            <div className="bg-red-900/30 backdrop-blur-lg rounded-xl border-2 border-red-400/60 p-10 text-center">
              <div className="text-6xl mb-4">🔒</div>
              <h2 className="text-3xl font-bold text-white mb-3">{t('admin.billing.lockedTitle')}</h2>
              <p className="text-red-200 mb-8 max-w-lg mx-auto">{t('admin.billing.lockedMessage')}</p>
              {/* Bill summary */}
              <div className="bg-white/10 rounded-xl p-6 max-w-sm mx-auto mb-8">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-purple-200">{t('admin.billing.bowlerCount', { count: billingStatus.bowlerCount })}</span>
                  <span className="text-white">{t('admin.billing.priceBreakdown', { price: billingStatus.pricePerBowler })}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-purple-200">{t('admin.billing.alreadyPaid')}</span>
                  <span className="text-green-400">- ${billingStatus.totalPaid.toFixed(2)}</span>
                </div>
                <div className="border-t border-white/20 pt-3 flex justify-between font-bold text-xl">
                  <span className="text-white">{t('admin.billing.totalDue')}</span>
                  <span className="text-red-300">${billingStatus.currentDue.toFixed(2)}</span>
                </div>
              </div>
              {/* Fairness message */}
              <p className="text-purple-200/80 text-xs italic max-w-lg mx-auto mb-8 leading-relaxed">{t('admin.billing.infoLine5')}</p>
              <button
                onClick={() => navigate('/admin/billing/checkout')}
                className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold px-10 py-4 rounded-xl text-lg transition duration-200"
              >
                💳 {t('admin.billing.lockedPayButton')}
              </button>
            </div>
          ) : (
            <>
          {/* Welcome Card */}
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20">
            <div className="text-center">
              <h2 className="text-4xl font-bold text-white mb-4">⚙️ {t('admin.dashboard.adminConsole')}</h2>
              <p className="text-purple-200 text-lg mb-6">
                {t('admin.dashboard.welcomeMessage')}
              </p>
            </div>
          </div>

          {/* Admin Actions Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:bg-white/15 transition duration-300 cursor-pointer group"
              onClick={() => navigate('/admin/leagues/create')}>
              <div className="text-center">
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-200">🏆</div>
                <h3 className="text-xl font-bold text-white mb-2">{t('admin.dashboard.createLeague.title')}</h3>
                <p className="text-purple-200 text-sm">
                  {t('admin.dashboard.createLeague.description')}
                </p>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:bg-white/15 transition duration-300 cursor-pointer group"
              onClick={() => navigate('/admin/leagues/manage')}>
              <div className="text-center">
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-200">📋</div>
                <h3 className="text-xl font-bold text-white mb-2">{t('admin.dashboard.manageLeagues.title')}</h3>
                <p className="text-purple-200 text-sm">
                  {t('admin.dashboard.manageLeagues.description')}
                </p>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:bg-white/15 transition duration-300 cursor-pointer group"
              onClick={() => navigate('/admin/bowlers/manage')}>
              <div className="text-center">
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-200">👥</div>
                <h3 className="text-xl font-bold text-white mb-2">{t('admin.dashboard.manageBowlers.title')}</h3>
                <p className="text-purple-200 text-sm">
                  {t('admin.dashboard.manageBowlers.description')}
                </p>
              </div>
            </div>

            <div
              className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:bg-white/15 transition duration-300 cursor-pointer group"
              onClick={() => navigate('/admin/analytics')}
            >
              <div className="text-center">
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-200">📊</div>
                <h3 className="text-xl font-bold text-white mb-2">{t('admin.dashboard.analytics.title')}</h3>
                <p className="text-purple-200 text-sm">
                  {t('admin.dashboard.analytics.description')}
                </p>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-200 text-sm">{t('admin.dashboard.statistics.activeLeagues')}</p>
                  <p className="text-3xl font-bold text-white">
                    {loading ? '...' : dashboardData?.stats?.activeLeagues || 0}
                  </p>
                </div>
                <div className="text-4xl">🎳</div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-200 text-sm">{t('admin.dashboard.statistics.registeredBowlers')}</p>
                  <p className="text-3xl font-bold text-white">
                    {loading ? '...' : dashboardData?.stats?.registeredBowlers || 0}
                  </p>
                </div>
                <div className="text-4xl">👤</div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-200 text-sm">{t('admin.dashboard.statistics.totalEarnings')}</p>
                  <p className="text-3xl font-bold text-white">
                    {loading ? '...' : `$${dashboardData?.stats?.totalEarnings || 0}`}
                  </p>
                </div>
                <div className="text-4xl">💵</div>
              </div>
            </div>
          </div>

          

          {/* youtube tutorial */}
          <div className="mt-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="bg-red-100 rounded-lg p-1.5 flex-shrink-0">
                <svg className="w-4 h-4 text-red-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                </svg>
              </div>
              <h2 className="text-sm font-bold text-white uppercase tracking-widest">League Administrator Tutorials</h2>
            </div>
            <div className="bg-red-800/30 rounded-2xl shadow-lg border border-red-900/30 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <p className="text-sm text-white mt-0.5">Find everything you need on the youtube playlist "BowlerPro: League Administrator tutorials" to learn how to use the system!</p>
              </div>
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/wi76IAtb6HM"
                  title="League Administrator Tutorial"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>

          {/* ── BILLING WIDGET — hidden during beta (currentDue=0 always) ─ */}
          {!billingLoading && billingStatus && (billingStatus.currentDue > 0 || billingStatus.isOverdue) && (
            <div
              className={`cursor-pointer rounded-xl border-2 p-6 transition duration-200 ${
                billingStatus.isOverdue && billingStatus.currentDue > 0
                  ? 'bg-red-900/20 border-red-400/60 hover:bg-red-900/30'
                  : billingStatus.currentDue > 0
                  ? 'bg-yellow-900/20 border-yellow-400/50 hover:bg-yellow-900/30'
                  : 'bg-green-900/20 border-green-400/40 hover:bg-green-900/30'
              }`}
              onClick={() => navigate('/admin/billing/checkout')}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                {/* Left: title + breakdown */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">💳</span>
                    <p className="text-white font-bold text-lg">{t('admin.dashboard.billing.widgetTitle')}</p>
                    {billingStatus.isOverdue && billingStatus.currentDue > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{t('admin.dashboard.billing.overdueWarning')}</span>
                    )}
                  </div>
                  <p className="text-purple-300 text-sm">
                    {t('admin.dashboard.billing.dueBy', { date: t(PERIOD_KEY[billingStatus.billingPeriod.label] || 'admin.dashboard.billing.periodDecember') })}
                    {' '}• {billingStatus.bowlerCount} {t('admin.dashboard.billing.bowlers')} × ${billingStatus.pricePerBowler}
                  </p>
                  {/* Billing info – always visible */}
                  <div className="mt-3 bg-white/5 rounded-lg p-3 border border-white/10 space-y-1 text-xs text-purple-200" onClick={e => e.stopPropagation()}>
                  <p>{t('admin.dashboard.billing.infoLine1', { price: billingStatus.pricePerBowler })}</p>
                  <p className="italic mb-2">{t('admin.dashboard.billing.infoLine5')}</p>
                 
                    
                    
                    <p>{t('admin.dashboard.billing.infoLine2')}</p>
                    <p>{t('admin.dashboard.billing.infoLine3')}</p>
                    <p>{t('admin.dashboard.billing.infoLine4')}</p>
                    
                  </div>
                </div>
                {/* Right: Amount */}
                <div className="text-right shrink-0">
                  {billingStatus.currentDue > 0 ? (
                    <>
                      <p className={`text-3xl font-bold ${billingStatus.isOverdue ? 'text-red-300' : 'text-yellow-300'}`}>
                        ${billingStatus.currentDue.toFixed(2)}
                      </p>
                      <p className="text-purple-300 text-xs">{t('admin.dashboard.billing.totalDue')}</p>
                      <p className="text-green-400 text-xs mt-1">{t('admin.dashboard.billing.alreadyPaid')}: ${billingStatus.totalPaid.toFixed(2)}</p>
                    </>
                  ) : (
                    <p className="text-green-400 text-xl font-bold">{t('admin.dashboard.billing.billClear')}</p>
                  )}
                </div>
              </div>
              {billingStatus.isOverdue && billingStatus.currentDue > 0 && (
                <p className="text-red-200 text-sm mt-3 border-t border-red-400/30 pt-3">{t('admin.dashboard.billing.overdueMessage')}</p>
              )}
            </div>
          )}
          </>
          )}

          {/* Recent Leagues Management */}
          {!loading && dashboardData?.recentLeagues && dashboardData.recentLeagues.length > 0 && (
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">{t('admin.dashboard.recentLeagues')}</h3>
                <button
                  onClick={() => navigate('/admin/leagues/manage')}
                  className="text-purple-300 hover:text-white text-sm font-medium"
                >
                  {t('admin.dashboard.viewAll')} →
                </button>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {dashboardData.recentLeagues.map((league) => (
                  <div
                    key={league.id}
                    className="bg-white/5 rounded-lg p-4 border border-white/10 hover:bg-white/10 transition cursor-pointer"
                    onClick={() => navigate('/admin/leagues/manage')}
                  >
                    <h4 className="font-semibold text-white mb-2">{league.name}</h4>
                    <div className="text-sm text-purple-300 space-y-1">
                      <p>{t('admin.dashboard.format')}: {league.teamFormat}</p>
                      <p>{t('admin.dashboard.start')}: {new Date(league.startDate).toLocaleDateString()}</p>
                      <p>{t('admin.dashboard.created')}: {new Date(league.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Admin Info */}
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">{t('admin.dashboard.administratorInformation')}</h3>
              <button
                onClick={() => setShowChangePasswordModal(true)}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-2 rounded-lg transition group"
              >
                <svg className="w-6 h-6 text-white/60 group-hover:rotate-45 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-sm font-semibold text-white/70 group-hover:text-white transition">Change Password</span>
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4 text-purple-200">
              <div>
                <p><strong className="text-white">{t('admin.dashboard.email')}:</strong> {user.email}</p>
                <p><strong className="text-white">{t('admin.dashboard.role')}:</strong> {t('admin.dashboard.administrator')}</p>
              </div>
              <div>
                <p><strong className="text-white">{t('admin.dashboard.accessLevel')}:</strong> {t('admin.dashboard.fullAccess')}</p>
                <p><strong className="text-white">{t('admin.dashboard.lastLogin')}:</strong> {t('admin.dashboard.justNow')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showChangePasswordModal && (
        <ChangePasswordModal accentColor="purple" onClose={() => setShowChangePasswordModal(false)} />
      )}
    </div>
  );
};

export default AdminDashboard;
